/**
 * TTS Playback player for Ragsmith.
 *
 * Two distinct code paths in this module:
 *
 *   1. Per-message playback (initTtsPlayer / handlePlayRequest):
 *      User clicks the speaker icon on a chat response. We POST text+voice
 *      to /voice/tts/prepare which returns a short id, then point the
 *      <audio> element at /voice/tts/mp3/{id}?token=... The browser's
 *      native media pipeline handles the streaming, seeking, range
 *      requests, and download — none of this passes through JS fetch().
 *      Why: Chromium kills long-running fetch+Blob accumulations at ~15 s
 *      regardless of how cleanly the server is streaming (verified
 *      reproducible in both Chrome and Brave; curl through the same
 *      nginx+tunnel path completes the same payload in 23 s). Using
 *      audio.src=URL sidesteps the fetch path entirely.
 *      Position is persisted in localStorage so accidental refreshes
 *      resume where they left off.
 *
 *   2. Initial-prompt autoplay (streamTtsPlayback):
 *      A separate Web-Audio-based streaming path used by chat.js for
 *      autoplaying the agent's opening message. Keeps the float32
 *      streaming endpoint (/voice/tts/stream) because that path needs
 *      programmatic mute/unmute via a GainNode. Left untouched in this
 *      rewrite.
 */

import { API_BASE, state } from './state.js';
import { getHeaders } from './api.js';
import { getAuthToken } from './auth.js';
import { getSelectedVoice, getTTSOptions } from './voice-manager.js';
import { fmtNarrationLength, syncSavedAudio } from './ui.js';
import { isMobileBrowser, silentWavUrl } from './silent-audio.js';
import { confirm as confirmDialog } from './confirm-dialog.js';

// ─── Per-Message Player State ────────────────────────────────────────────────
let playerEl = null;             // Sticky player DOM
let audioEl = null;              // <audio> element doing the actual playback
let currentButton = null;        // The .tts-btn that triggered playback
let currentHash = null;          // text+voice hash of what's loaded
let currentTtsUrl = null;        // Active /voice/tts/mp3/{id}?token=... URL
let prepareAbort = null;         // Cancels in-flight /voice/tts/prepare fetch
// The render itself runs as a detached job on the server, so aborting the
// fetch above only stops us WAITING for it. Keep its id to call it off.
let renderingId = null;
let lastRenderProgress = 0;      // 0..1, for the "discard this?" prompt
let positionSaveTimer = null;    // Periodic localStorage save while playing
let isDraggingSeek = false;      // True while user is mid-drag on the slider
let isLoading = false;           // True while prepare→audio.canplay is pending

// Hash → URL cache so repeat clicks on the same message reuse the same
// audio.src. Bounded; entries past the cap drop their URL (browser HTTP
// cache may still serve them anyway). Browser handles the actual MP3 bytes —
// we no longer accumulate Blobs in JS.
const urlCache = new Map();
const URL_CACHE_MAX = 12;

// localStorage scheme for position persistence
const POSITION_PREFIX = 'tts-pos-';
const POSITION_TTL_MS = 30 * 24 * 60 * 60 * 1000;  // 30 days

// Float32 cache for the autoplay (streamTtsPlayback) path. Separate from
// urlCache because that path uses raw PCM, not MP3.
const audioCache = new Map();

// Mobile keepalive — silent <audio> element prevents browser suspension on screen-off
let ttsKeepAliveAudio = null;

// ─── Initialization ──────────────────────────────────────────────────────────

export function initTtsPlayer() {
    window.addEventListener('tts-play', (e) => {
        const { text, messageEl, button } = e.detail;
        handlePlayRequest(text, messageEl, button);
    });
    // Save position before leaving so accidental refreshes can resume.
    window.addEventListener('beforeunload', () => savePosition());
    window.addEventListener('pagehide', () => savePosition());
    // Garbage-collect old position entries on init so localStorage doesn't
    // grow unbounded across long-lived sessions.
    prunePositions();
}

// ─── Mobile Keepalive ────────────────────────────────────────────────────────

function startTtsKeepAlive() {
    // Desktop browsers do not suspend a page for a dark screen, so the
    // silent companion track is only for phones. See silent-audio.js for
    // what the old zero-length looping clip did to the browser on pause.
    if (isMobileBrowser()) {
        if (!ttsKeepAliveAudio) {
            ttsKeepAliveAudio = new Audio(silentWavUrl());
            ttsKeepAliveAudio.loop = true;
            ttsKeepAliveAudio.volume = 0;
        }
        ttsKeepAliveAudio.play().catch(() => {});
    }
    if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = new MediaMetadata({
            title: 'Playback', artist: 'Ragsmith'
        });
        navigator.mediaSession.playbackState = 'playing';
        navigator.mediaSession.setActionHandler('pause', () => {
            if (audioEl && !audioEl.paused) audioEl.pause();
        });
        navigator.mediaSession.setActionHandler('play', () => {
            if (audioEl && audioEl.paused) audioEl.play().catch(() => {});
        });
    }
}

function stopTtsKeepAlive() {
    if (ttsKeepAliveAudio) ttsKeepAliveAudio.pause();
    if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('play', null);
    }
}

// ─── Position Persistence (localStorage) ─────────────────────────────────────

function loadPosition(hash) {
    try {
        const raw = localStorage.getItem(POSITION_PREFIX + hash);
        if (!raw) return null;
        const data = JSON.parse(raw);
        // Don't restore if at the very end (within 1s of duration) — the
        // user finished it last time, don't bounce them to the end again.
        if (data.duration && data.position >= data.duration - 1) return null;
        return Number(data.position) || null;
    } catch { return null; }
}

function savePosition() {
    if (!currentHash || !audioEl || !isFinite(audioEl.currentTime)) return;
    try {
        localStorage.setItem(POSITION_PREFIX + currentHash, JSON.stringify({
            position: audioEl.currentTime,
            duration: audioEl.duration || 0,
            savedAt: Date.now(),
        }));
    } catch { /* localStorage full or disabled — silently skip */ }
}

function clearPosition(hash) {
    try { localStorage.removeItem(POSITION_PREFIX + hash); } catch {}
}

function prunePositions() {
    try {
        const now = Date.now();
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith(POSITION_PREFIX)) continue;
            try {
                const data = JSON.parse(localStorage.getItem(key) || '{}');
                if (now - (data.savedAt || 0) > POSITION_TTL_MS) toRemove.push(key);
            } catch { toRemove.push(key); }
        }
        for (const k of toRemove) localStorage.removeItem(k);
    } catch { /* skip */ }
}

// ─── URL Cache (prepared TTS URL per text+voice) ─────────────────────────────

function cacheUrl(hash, url) {
    // Evict oldest entries when full. The browser's HTTP cache still holds
    // the actual bytes; we're only tracking which URL to use for which hash.
    while (urlCache.size >= URL_CACHE_MAX) {
        const oldestKey = urlCache.keys().next().value;
        urlCache.delete(oldestKey);
    }
    urlCache.set(hash, url);
    return url;
}

// ─── Play Request Handler ────────────────────────────────────────────────────

async function handlePlayRequest(text, messageEl, button) {
    const voice = getSelectedVoice();
    const hash = simpleHash(text + '|' + voice);

    // If a request for THIS exact hash is already loading, ignore the click —
    // re-triggering it would abort the in-flight prepare and start a new one
    // mid-flight.
    if (isLoading && currentHash === hash) {
        return;
    }

    // Same button while playing → toggle pause
    if (currentHash === hash && audioEl && !audioEl.paused) {
        audioEl.pause();
        return;
    }
    // Same button while paused (and src still loaded) → resume
    if (currentHash === hash && audioEl && audioEl.paused && audioEl.src) {
        audioEl.play().catch(() => {});
        return;
    }

    // New play request — tear down anything currently playing
    stopPlayback();
    currentButton = button;
    currentHash = hash;
    isLoading = true;
    button.classList.add('loading');
    showPlayer();
    updatePlayerState('loading');

    // URL cache hit → reuse the prepared URL (browser may serve from HTTP
    // cache without going back to the server).
    const cachedUrl = urlCache.get(hash);
    if (cachedUrl) {
        try {
            await startPlayback(cachedUrl, hash);
            button.classList.remove('loading');
        } catch (e) {
            console.error('[TTS Player] Cached playback failed:', e);
            button.classList.remove('loading');
            hidePlayer();
        } finally {
            isLoading = false;
        }
        return;
    }

    // A narration saved with this message plays from storage: no render, no
    // wait, the same audio after a reload or a server restart.
    const savedId = messageEl?.dataset?.narrationMediaId;
    if (savedId) {
        const url = `${API_BASE}/chat/media/${savedId}/stream${authQuery()}`;
        cacheUrl(hash, url);
        try {
            await startPlayback(url, hash);
        } catch (e) {
            console.error('[TTS Player] Saved narration failed:', e);
            hidePlayer();
        } finally {
            // Same discipline as the cached-URL path: while this flag is
            // set every click, pause included, is ignored as a duplicate.
            isLoading = false;
            button.classList.remove('loading');
        }
        return;
    }

    // Two-step: POST text+voice to /prepare, get back a short id, then point
    // the <audio> element at /voice/tts/mp3/{id}. The actual MP3 bytes are
    // streamed by the browser's media pipeline — never touched by JS fetch.
    const myAbort = new AbortController();
    prepareAbort = myAbort;
    try {
        const messageId = messageEl?.dataset?.messageId;
        const res = await fetch(`${API_BASE}/voice/tts/prepare`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
                text, voice, ...getTTSOptions(voice),
                message_id: messageId ? Number(messageId) : undefined,
                // Audio made from a saved message is kept with it, so any
                // device opening the conversation plays the same recording
                // instead of spending the render again. Narrator mode always
                // keeps; anything else leaves it to the server, which keeps a
                // read long enough to be worth returning to and lets a glance
                // go. Text with no message behind it is never stored.
                persist: messageId ? (state.narratorMode ? true : 'auto') : false,
            }),
            signal: myAbort.signal,
        });
        if (!res.ok) throw new Error(`TTS prepare failed: ${res.status}`);
        const { id } = await res.json();
        if (!id) throw new Error('Prepare returned no id');
        renderingId = id;
        lastRenderProgress = 0;

        // If we've been superseded by a later request, drop on the floor.
        if (myAbort.signal.aborted || currentHash !== hash) {
            return;
        }

        // Auth via query param — the auth middleware accepts ?token= (session),
        // ?u= (container), or ?c= (capsule). <audio> tags can't set custom
        // headers, so the param is the only way to authenticate the request.
        const authToken = getAuthToken();
        let authParam = '';
        if (authToken) {
            authParam = `?token=${encodeURIComponent(authToken)}`;
        } else if (state.containerMode && state.containerToken) {
            authParam = `?u=${encodeURIComponent(state.containerToken)}`;
        } else if (state.capsuleMode && state.capsuleToken) {
            authParam = `?c=${encodeURIComponent(state.capsuleToken)}`;
        }
        let url = `${API_BASE}/voice/tts/mp3/${id}${authParam}`;
        // Render on the server as a job and wait for it here, with a clock.
        // Pointing <audio> at an unrendered id held one request open for
        // the whole synthesis; a slow speaker on a long chapter outlived
        // nginx's patience and the player reported a media error.
        const done = await waitForRender(id, myAbort.signal, hash);
        renderingId = null;  // finished on its own; nothing to call off
        if (myAbort.signal.aborted || currentHash !== hash) return;
        if (done?.media_id && messageEl) {
            // Saved with the message: play the stored copy from now on, and
            // let the button say so, with the length.
            messageEl.dataset.narrationMediaId = done.media_id;
            if (done.duration_s) messageEl.dataset.narrationSeconds = String(Math.round(done.duration_s));
            markSavedButton(button, messageEl.dataset.narrationSeconds);
            syncSavedAudio(messageEl);
            url = `${API_BASE}/chat/media/${done.media_id}/stream${authParam}`;
        }
        cacheUrl(hash, url);
        await startPlayback(url, hash);
        button.classList.remove('loading');
    } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('[TTS Player] Prepare error:', e);
        setLoadingText(`Could not generate audio: ${e.message || e}`);
        button.classList.remove('loading');
        if (currentHash === hash) {
            hidePlayer();
            currentHash = null;
        }
    } finally {
        if (prepareAbort === myAbort) {
            isLoading = false;
            prepareAbort = null;
        }
    }
}

async function startPlayback(url, hash) {
    if (!audioEl) return;
    currentTtsUrl = url;
    audioEl.src = url;
    // Restore saved position before play() if applicable.
    const savedPos = loadPosition(hash);
    if (savedPos != null && savedPos > 0) {
        await new Promise((resolve) => {
            const onReady = () => {
                audioEl.removeEventListener('loadedmetadata', onReady);
                try { audioEl.currentTime = savedPos; } catch {}
                resolve();
            };
            if (isFinite(audioEl.duration)) {
                try { audioEl.currentTime = savedPos; } catch {}
                resolve();
            } else {
                audioEl.addEventListener('loadedmetadata', onReady, { once: true });
            }
        });
    }
    await audioEl.play();
    startPositionSaveTimer();
    startTtsKeepAlive();
}

// ─── Sticky Player UI ────────────────────────────────────────────────────────

function showPlayer() {
    if (playerEl) {
        playerEl.classList.remove('hidden');
        return;
    }

    playerEl = document.createElement('div');
    playerEl.id = 'tts-player';
    playerEl.className = 'tts-player';
    playerEl.innerHTML = `
        <button class="tts-player-btn tts-play-pause" title="Play / Pause" aria-label="Play / Pause">
            <svg class="tts-icon-play" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            <svg class="tts-icon-pause tts-icon-hidden" viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
                <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
            </svg>
            <svg class="tts-icon-spinner tts-icon-hidden" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <circle cx="12" cy="12" r="9" stroke-opacity="0.2"/>
                <path d="M21 12a9 9 0 0 0-9-9"/>
            </svg>
        </button>
        <span class="tts-time tts-time-current">0:00</span>
        <div class="tts-seek-wrap">
            <input type="range" class="tts-seek" min="0" max="1" step="0.01" value="0" aria-label="Seek"/>
            <div class="tts-loading-overlay">
                <div class="tts-loading-fill"></div>
                <span class="tts-loading-text">Generating audio</span>
                <span class="tts-loading-dots"><i></i><i></i><i></i></span>
            </div>
        </div>
        <span class="tts-time tts-time-total">0:00</span>
        <button class="tts-player-btn tts-download" title="Download MP3" aria-label="Download MP3">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
        </button>
        <button class="tts-player-btn tts-close" title="Close" aria-label="Close">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
        </button>
    `;

    const inputArea = document.getElementById('input-area');
    inputArea.parentNode.insertBefore(playerEl, inputArea);

    // Single hidden <audio> element drives everything.
    audioEl = new Audio();
    audioEl.preload = 'auto';

    // Wire native audio events → our UI.
    //
    // Use 'playing' instead of 'play' to exit the loading state. The 'play'
    // event fires when audio.paused flips false (i.e., right after play() is
    // called) regardless of whether sound is actually coming out — on mobile,
    // buffering happens *after* this, so listening to 'play' clears the
    // spinner before the audio is ready and the user sees 0:00 / 0:00 for a
    // beat before playback starts. 'playing' fires when actual playback
    // begins, which is what we want.
    //
    // 'waiting' fires when playback stalls for buffering mid-stream. Re-show
    // the spinner so the user knows we're loading more, not frozen.
    audioEl.addEventListener('playing', () => {
        playerEl?.classList.remove('first-load');
        if (ttsKeepAliveAudio && isMobileBrowser()) ttsKeepAliveAudio.play().catch(() => {});
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
        updatePlayerState('playing');
    });
    // A stall mid-stream shows the spinner but leaves the time, the slider
    // and the controls alone: it is a pause in data, not a new load.
    audioEl.addEventListener('waiting', () => updatePlayerState('stalled'));
    audioEl.addEventListener('pause', () => {
        savePosition();
        // Paused means paused everywhere: the OS media controls learn it,
        // and the silent companion track stops instead of looping alone.
        if (ttsKeepAliveAudio) ttsKeepAliveAudio.pause();
        if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
        if (audioEl && audioEl.ended) return;
        updatePlayerState('paused');
    });
    audioEl.addEventListener('ended', () => {
        clearPosition(currentHash);
        stopPositionSaveTimer();
        stopTtsKeepAlive();
        updatePlayerState('ended');
    });
    audioEl.addEventListener('timeupdate', () => {
        if (!isDraggingSeek) updateTimeAndSeek();
    });
    audioEl.addEventListener('loadedmetadata', () => updateTimeAndSeek());
    audioEl.addEventListener('error', (e) => {
        console.error('[TTS Player] Audio error:', audioEl.error || e);
        updatePlayerState('error');
    });

    // UI controls
    playerEl.querySelector('.tts-play-pause').addEventListener('click', togglePlayPause);
    playerEl.querySelector('.tts-close').addEventListener('click', closePlayerConfirmed);
    playerEl.querySelector('.tts-download').addEventListener('click', triggerDownload);

    const seek = playerEl.querySelector('.tts-seek');
    // input event = real-time scrubbing; pointerdown/up bracket the drag so
    // we suspend timeupdate->slider sync during drag (prevents jitter).
    seek.addEventListener('pointerdown', () => { isDraggingSeek = true; });
    seek.addEventListener('pointerup', () => { isDraggingSeek = false; });
    seek.addEventListener('input', onSeekInput);
    seek.addEventListener('change', onSeekChange);
}

function hidePlayer() {
    if (playerEl) playerEl.classList.add('hidden');
}

function updatePlayerState(state) {
    if (!playerEl) return;
    const playIcon = playerEl.querySelector('.tts-icon-play');
    const pauseIcon = playerEl.querySelector('.tts-icon-pause');
    const spinnerIcon = playerEl.querySelector('.tts-icon-spinner');
    const currentTimeEl = playerEl.querySelector('.tts-time-current');
    const totalTimeEl = playerEl.querySelector('.tts-time-total');
    const seek = playerEl.querySelector('.tts-seek');

    // Helper: show exactly one of the three play/pause/spinner icons
    const showIcon = (which) => {
        playIcon.classList.toggle('tts-icon-hidden', which !== 'play');
        pauseIcon.classList.toggle('tts-icon-hidden', which !== 'pause');
        spinnerIcon.classList.toggle('tts-icon-hidden', which !== 'spinner');
    };

    if (state === 'loading') {
        playerEl.classList.add('loading', 'first-load');
        showIcon('spinner');
        if (currentTimeEl) currentTimeEl.textContent = '';
        if (totalTimeEl) totalTimeEl.textContent = '';
        if (seek) seek.value = 0;
        lastShownSecond = -1;
        return;
    }
    if (state === 'stalled') {
        showIcon('spinner');
        return;
    }

    // Any non-loading state: drop the loading class to unlock controls
    playerEl.classList.remove('loading');

    if (state === 'playing') {
        showIcon('pause');
        if (currentButton) currentButton.classList.add('playing');
    } else if (state === 'paused' || state === 'ended' || state === 'stopped') {
        showIcon('play');
        if (currentButton) currentButton.classList.remove('playing');
    } else if (state === 'error') {
        showIcon('play');
        if (currentTimeEl) currentTimeEl.textContent = 'err';
    }
}

let lastShownSecond = -1;
let lastShownDuration = -1;

function updateTimeAndSeek(force = false) {
    if (!playerEl || !audioEl) return;
    const cur = audioEl.currentTime || 0;
    const dur = isFinite(audioEl.duration) ? audioEl.duration : 0;
    // timeupdate fires about four times a second; the display only changes
    // once a second. Writing the DOM only when the shown value changes
    // keeps a long playback from being a steady stream of style recalcs.
    const sec = Math.floor(cur);
    if (!force && sec === lastShownSecond && dur === lastShownDuration) return;
    lastShownSecond = sec;
    lastShownDuration = dur;
    const currentTimeEl = playerEl.querySelector('.tts-time-current');
    const totalTimeEl = playerEl.querySelector('.tts-time-total');
    const seek = playerEl.querySelector('.tts-seek');
    if (currentTimeEl) currentTimeEl.textContent = formatTime(cur);
    if (totalTimeEl) totalTimeEl.textContent = formatTime(dur);
    if (seek && dur > 0) {
        if (Number(seek.max) !== dur) seek.max = dur;
        seek.value = cur;
    }
}

// ─── Controls ───────────────────────────────────────────────────────────────

function togglePlayPause() {
    // During loading the audio isn't ready yet — ignore the click rather
    // than re-triggering the speaker button (which would abort the
    // in-flight fetch and start a new one mid-flight, racing the abort
    // catch against the new request and hiding the player).
    if (isLoading) return;
    if (!audioEl || !audioEl.src) {
        // Player is up but truly nothing loaded (cache miss + closed) —
        // re-trigger via the message's speaker button.
        if (currentButton) currentButton.click();
        return;
    }
    if (audioEl.paused) audioEl.play().catch(() => {});
    else audioEl.pause();
}

function onSeekInput(e) {
    if (!audioEl || !isFinite(audioEl.duration)) return;
    // Update the time display immediately as the user drags
    const v = parseFloat(e.target.value);
    const currentTimeEl = playerEl.querySelector('.tts-time-current');
    if (currentTimeEl) currentTimeEl.textContent = formatTime(v);
}

function onSeekChange(e) {
    if (!audioEl || !isFinite(audioEl.duration)) return;
    const v = parseFloat(e.target.value);
    try { audioEl.currentTime = v; } catch {}
    savePosition();
}

function triggerDownload() {
    if (!currentTtsUrl || !currentHash) return;
    const a = document.createElement('a');
    a.href = currentTtsUrl;
    // Use a date-stamped name + hash suffix. Browser will fetch from the
    // same /voice/tts/mp3/{id}?token=... URL (likely served from HTTP cache
    // if playback already touched it).
    const ts = new Date().toISOString().slice(0, 10);
    a.download = `ragsmith-tts-${ts}-${currentHash.slice(0, 8)}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

/**
 * Close, but ask first if it would throw work away.
 *
 * Only while a render is actually in flight: that audio does not exist yet
 * and stopping loses every minute already spent on it, which on a chapter is
 * a real loss. Once it is rendered there is nothing to lose — the audio is
 * made, and asking every time would just be a door to push open.
 */
async function closePlayerConfirmed() {
    if (!renderingId) {
        closePlayer();
        return;
    }
    const pct = lastRenderProgress > 0 ? ` (about ${Math.round(lastRenderProgress * 100)}% done)` : '';
    const ok = await confirmDialog({
        title: 'Stop rendering?',
        message: `The audio is still being made${pct}. Stopping discards it, and playing this again starts the render from the beginning.`,
        confirmText: 'Stop',
        cancelText: 'Keep going',
    });
    if (ok) closePlayer();
}

function closePlayer() {
    stopPlayback();
    hidePlayer();
}

/**
 * Call off a render nobody is waiting for.
 *
 * Aborting the fetch only ends our side of the conversation: the render is a
 * detached task on the server and used to run to completion regardless, so
 * pressing stop on a long chapter left a voice worker busy for minutes
 * producing audio for a listener who had gone. `keepalive` so it still goes
 * out if the page is being unloaded.
 */
function cancelRender() {
    const id = renderingId;
    renderingId = null;
    if (!id) return;
    fetch(`${API_BASE}/voice/tts/render/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
        keepalive: true,
    }).catch(() => {
        /* best effort: the render expires on its own soon enough */
    });
}

function stopPlayback() {
    stopPositionSaveTimer();
    stopTtsKeepAlive();
    if (prepareAbort) {
        prepareAbort.abort();
        prepareAbort = null;
    }
    cancelRender();
    if (audioEl) {
        try {
            audioEl.pause();
            audioEl.removeAttribute('src');
            audioEl.load();  // Releases the source
        } catch {}
    }
    if (currentButton) currentButton.classList.remove('playing', 'loading');
    currentButton = null;
    currentHash = null;
    currentTtsUrl = null;
    updatePlayerState('stopped');
}

function startPositionSaveTimer() {
    stopPositionSaveTimer();
    // Save every 5s during playback so a refresh loses at most 5s of position.
    positionSaveTimer = setInterval(savePosition, 5000);
}

function stopPositionSaveTimer() {
    if (positionSaveTimer) {
        clearInterval(positionSaveTimer);
        positionSaveTimer = null;
    }
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function formatTime(secs) {
    if (!isFinite(secs) || secs <= 0) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

function simpleHash(str) {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash + str.charCodeAt(i)) & 0xffffffff;
    }
    return hash.toString(36);
}

// ─── Programmatic TTS for Initial Prompt Autoplay ───────────────────────────

/**
 * Stream TTS audio for text, returning a controller.
 * - Preloads `minChunks` audio chunks before resolving the ready promise.
 * - Caller can mute/unmute and stop playback.
 *
 * This path stays on the float32 streaming endpoint (rather than MP3)
 * because it needs programmatic mute/unmute via a Web Audio GainNode —
 * something the <audio>-element-based per-message player doesn't need.
 *
 * @param {string} text - Full text to synthesize
 * @returns {{ ready: Promise<void>, play: Function, stop: Function, mute: Function, unmute: Function, isMuted: Function }}
 */
export function streamTtsPlayback(text) {
    const voice = getSelectedVoice();
    let ctx = null;
    let schedTime = 0;
    let muted = false;
    let gainNode = null;
    let stopped = false;
    let resolveReady = null;
    const MIN_PRELOAD_CHUNKS = 3;
    let chunksReceived = 0;
    let allChunks = [];
    // Kept only to build the replay cache below. Past this many samples the
    // read is not worth caching (two minutes at 24 kHz is ~11MB of float32;
    // a chapter was 75MB, held forever), so retention simply stops.
    const MAX_CACHE_SAMPLES = 24000 * 120;
    let retained = 0;

    const ready = new Promise(resolve => { resolveReady = resolve; });

    // Start fetch immediately. The abort matters as much as the fetch: the
    // server stops synthesizing when the client goes away, and without this it
    // never learns that we did — stop() closed the speaker and left a worker
    // reading the rest of the text to nobody.
    const streamAbort = new AbortController();
    let reader = null;
    const fetchPromise = fetch(`${API_BASE}/voice/tts/stream`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ text, voice }),
        signal: streamAbort.signal,
    });

    // Process stream in background
    (async () => {
        try {
            const res = await fetchPromise;
            if (!res.ok || stopped) {
                resolveReady?.();
                return;
            }

            const sampleRate = parseInt(res.headers.get('X-Sample-Rate') || '24000');
            ctx = new AudioContext({ sampleRate });
            gainNode = ctx.createGain();
            gainNode.gain.value = muted ? 0 : 1;
            gainNode.connect(ctx.destination);
            schedTime = ctx.currentTime;

            reader = res.body.getReader();
            let leftover = new Uint8Array(0);

            while (!stopped) {
                const { done, value } = await reader.read();
                if (done) break;

                const combined = new Uint8Array(leftover.length + value.length);
                combined.set(leftover);
                combined.set(value, leftover.length);

                const completeBytes = combined.length - (combined.length % 4);
                if (completeBytes > 0) {
                    const float32 = new Float32Array(
                        combined.buffer.slice(combined.byteOffset, combined.byteOffset + completeBytes)
                    );
                    if (retained <= MAX_CACHE_SAMPLES) {
                        allChunks.push(new Float32Array(float32));
                        retained += float32.length;
                    } else if (allChunks.length) {
                        allChunks = [];  // too long to cache: drop what was kept
                    }

                    // Schedule audio playback
                    const buffer = ctx.createBuffer(1, float32.length, sampleRate);
                    buffer.getChannelData(0).set(float32);
                    const source = ctx.createBufferSource();
                    source.buffer = buffer;
                    source.connect(gainNode);
                    const startAt = Math.max(ctx.currentTime, schedTime);
                    source.start(startAt);
                    schedTime = startAt + buffer.duration;

                    chunksReceived++;
                    if (chunksReceived === MIN_PRELOAD_CHUNKS) {
                        resolveReady?.();
                    }
                }
                leftover = combined.slice(completeBytes);
            }

            // If we never hit MIN_PRELOAD_CHUNKS (short text), resolve anyway
            resolveReady?.();

            // Cache for future replay
            const totalSamples = allChunks.reduce((n, c) => n + c.length, 0);
            if (totalSamples > 0 && retained <= MAX_CACHE_SAMPLES) {
                const fullAudio = new Float32Array(totalSamples);
                let offset = 0;
                for (const chunk of allChunks) { fullAudio.set(chunk, offset); offset += chunk.length; }
                // Bounded: a handful of recent reads, oldest out first.
                if (audioCache.size >= 3) audioCache.delete(audioCache.keys().next().value);
                audioCache.set(simpleHash(text + '|' + voice), { samples: fullAudio, sampleRate });
            }
        } catch (e) {
            // An abort is this function being told to stop, not a failure.
            if (e?.name !== 'AbortError') console.error('[TTS Autoplay] Stream error:', e);
            resolveReady?.();
        }
    })();

    return {
        /** Resolves when enough audio is preloaded to start text rendering */
        ready,
        /** Stop playback and release resources */
        stop() {
            stopped = true;
            allChunks = [];
            // Tell the server, not just ourselves.
            try { reader?.cancel(); } catch { /* already done */ }
            streamAbort.abort();
            if (ctx) { ctx.close().catch(() => {}); ctx = null; }
        },
        mute() {
            muted = true;
            if (gainNode) gainNode.gain.value = 0;
        },
        unmute() {
            muted = false;
            if (gainNode) gainNode.gain.value = 1;
            // Resume context if suspended (autoplay policy)
            if (ctx?.state === 'suspended') ctx.resume();
        },
        isMuted() { return muted; }
    };
}


// ─── Background render polling ───────────────────────────────────────

function setLoadingText(text) {
    const el = document.querySelector('.tts-loading-text');
    if (el) el.textContent = text;
}

function fmtElapsed(s) {
    const m = Math.floor(s / 60), r = s % 60;
    return m ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
}

async function waitForRender(id, signal, hash) {
    const start = await fetch(`${API_BASE}/voice/tts/render/${id}`, { method: 'POST', headers: getHeaders(), signal });
    if (!start.ok) throw new Error(`render start failed: ${start.status}`);
    let st = await start.json();
    let t0 = Date.now() - (st.elapsed_s || 0) * 1000;
    while (st.state === 'rendering' || st.state === 'idle') {
        if (signal.aborted || currentHash !== hash) throw Object.assign(new Error('superseded'), { name: 'AbortError' });
        const elapsed = fmtElapsed(Math.round((Date.now() - t0) / 1000));
        if (typeof st.progress === 'number' && st.progress > 0) {
            lastRenderProgress = st.progress;
            const pct = Math.round(st.progress * 100);
            const eta = st.eta_s != null ? ` · about ${fmtElapsed(st.eta_s)} left` : '';
            setLoadingText(`Generating audio · ${pct}%${eta}`);
            setLoadingFill(st.progress);
        } else {
            setLoadingText(`Generating audio · ${elapsed}`);
            setLoadingFill(0);
        }
        await new Promise(r => setTimeout(r, 3000));
        const res = await fetch(`${API_BASE}/voice/tts/render/${id}`, { headers: getHeaders(), signal });
        if (!res.ok) throw new Error(`render lost (${res.status})`);
        st = await res.json();
    }
    if (st.state === 'failed') throw new Error(st.error || 'synthesis failed');
    setLoadingText('Generating audio');
    setLoadingFill(0);
    return st;
}

function markSavedButton(button, secs) {
    button.classList.add('has-saved');
    const len = fmtNarrationLength(secs);
    button.title = `Play saved narration${len ? ` (${len})` : ''}`;
    let badge = button.querySelector('.tts-saved-len');
    if (len) {
        if (!badge) { badge = document.createElement('span'); badge.className = 'tts-saved-len'; button.appendChild(badge); }
        badge.textContent = len;
    }
    if (!button.querySelector('.tts-saved-x')) button.insertAdjacentHTML('beforeend', SAVED_X_HTML);
}

/** The X inside a saved-narration pill. A span, since the pill is a button. */
const SAVED_X_HTML = '<span class="tts-saved-x" role="button" tabindex="0" title="Delete this audio" aria-label="Delete this audio">'
    + '<svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>';

/**
 * Delete the audio saved with a reply and give the reply its plain play
 * button back. The text is not touched; pressing play makes the audio again.
 */
async function deleteSavedNarration(x) {
    const button = x.closest('.tts-btn');
    const messageEl = x.closest('.message');
    const mediaId = messageEl?.dataset?.narrationMediaId;
    if (!button || !mediaId) return;
    const len = fmtNarrationLength(messageEl.dataset.narrationSeconds);
    const ok = await confirmDialog({
        title: 'Delete this audio?',
        message: `The saved audio${len ? ` (${len})` : ''} for this reply will be deleted. The reply itself stays, and the play button can make the audio again.`,
        confirmText: 'Delete audio',
        cancelText: 'Keep it',
    });
    if (!ok) return;
    x.classList.add('busy');
    try {
        const res = await fetch(`${API_BASE}/voice/tts/narration/${mediaId}`, { method: 'DELETE', headers: getHeaders() });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
    } catch (e) {
        x.classList.remove('busy');
        await confirmDialog({ title: 'The audio was not deleted', message: e.message, confirmText: 'OK', cancelText: 'Close', danger: false });
        return;
    }
    // Anything still pointing at the deleted file: the player if it has it
    // loaded, and the remembered URL, or the next play would ask for a file
    // that is gone.
    const stale = `/chat/media/${mediaId}/stream`;
    if (audioEl?.src && audioEl.src.includes(stale)) closePlayer();
    for (const [hash, url] of urlCache) if (url.includes(stale)) urlCache.delete(hash);
    delete messageEl.dataset.narrationMediaId;
    delete messageEl.dataset.narrationSeconds;
    button.classList.remove('has-saved');
    button.title = 'Play audio';
    button.querySelector('.tts-saved-len')?.remove();
    x.remove();
    syncSavedAudio(messageEl);
}

// One listener for every pill, present and future. Capture phase: the X sits
// inside the play button, and a click on it must not also start playback.
document.addEventListener('click', (e) => {
    const x = e.target.closest?.('.tts-saved-x');
    if (!x) return;
    e.preventDefault();
    e.stopPropagation();
    deleteSavedNarration(x);
}, true);
document.addEventListener('keydown', (e) => {
    const x = e.target.closest?.('.tts-saved-x');
    if (!x || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    e.stopPropagation();
    deleteSavedNarration(x);
}, true);

function setLoadingFill(frac) {
    const el = document.querySelector('.tts-loading-fill');
    if (el) el.style.width = `${Math.max(0, Math.min(100, frac * 100))}%`;
}

/**
 * Speak arbitrary text the way a chat reply is spoken, and return a URL to
 * the finished audio.
 *
 * The voice panel's "Say it" used the one-shot /voice/tts call: a different
 * route from the chat player's, and one request held open for the whole
 * synthesis. This is the chat route (prepare, render as a job, poll, then
 * the prepared MP3), so what is auditioned is cleaned, paused at its line
 * breaks and rendered exactly as a reply will be, however long it is.
 * Nothing is stored: text with no message behind it never is.
 *
 * onProgress(text) gets a line worth showing while it renders. Aborting the
 * signal calls the render off on the server too.
 */
export async function renderSpeechUrl(text, voice, opts, { signal, onProgress } = {}) {
    const res = await fetch(`${API_BASE}/voice/tts/prepare`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ text, voice, ...opts, persist: false }),
        signal,
    });
    if (!res.ok) throw new Error(`TTS prepare failed: ${res.status}`);
    const { id } = await res.json();
    if (!id) throw new Error('Prepare returned no id');
    const callOff = () => {
        fetch(`${API_BASE}/voice/tts/render/${id}`, {
            method: 'DELETE', headers: getHeaders(), keepalive: true,
        }).catch(() => {});
    };
    signal?.addEventListener('abort', callOff, { once: true });
    try {
        const start = await fetch(`${API_BASE}/voice/tts/render/${id}`, { method: 'POST', headers: getHeaders(), signal });
        if (!start.ok) throw new Error(`render start failed: ${start.status}`);
        let st = await start.json();
        const t0 = Date.now() - (st.elapsed_s || 0) * 1000;
        while (st.state === 'rendering' || st.state === 'idle') {
            if (typeof st.progress === 'number' && st.progress > 0) {
                const eta = st.eta_s != null ? ` · about ${fmtElapsed(st.eta_s)} left` : '';
                onProgress?.(`Generating audio · ${Math.round(st.progress * 100)}%${eta}`);
            } else {
                onProgress?.(`Generating audio · ${fmtElapsed(Math.round((Date.now() - t0) / 1000))}`);
            }
            await new Promise((resolve, reject) => {
                const timer = setTimeout(resolve, 1500);
                signal?.addEventListener('abort', () => {
                    clearTimeout(timer);
                    reject(Object.assign(new Error('stopped'), { name: 'AbortError' }));
                }, { once: true });
            });
            const poll = await fetch(`${API_BASE}/voice/tts/render/${id}`, { headers: getHeaders(), signal });
            if (!poll.ok) throw new Error(`render lost (${poll.status})`);
            st = await poll.json();
        }
        if (st.state === 'failed') throw new Error(st.error || 'synthesis failed');
    } finally {
        signal?.removeEventListener('abort', callOff);
    }
    return `${API_BASE}/voice/tts/mp3/${id}${authQuery()}`;
}

function authQuery() {
    const authToken = getAuthToken();
    if (authToken) return `?token=${encodeURIComponent(authToken)}`;
    if (state.containerMode && state.containerToken) return `?u=${encodeURIComponent(state.containerToken)}`;
    if (state.capsuleMode && state.capsuleToken) return `?c=${encodeURIComponent(state.capsuleToken)}`;
    return '';
}
